// Offline-kartta: lataa näkyvän alueen karttaruudut puhelimen välimuistiin, jotta
// kartta näkyy maastossa ilman verkkoa. Latauslogiikka on lib/tileCache.js:ssä.
//
// Vain MML:n kartat ladataan: ne kulkevat oman palvelimen kautta, ja palvelin
// pitää kuormaa kurissa. OpenStreetMapin ruutupalvelimen käyttöehdot kieltävät
// massalataamisen, joten sille ei tarjota esilatausta.

import { useEffect, useRef, useState } from "preact/hooks";

import { fetchTileBlob } from "../lib/basemap.js";
import { logEvent } from "../lib/diagnostics.js";
import { APPROX_TILE_BYTES, iterateTiles, MAX_NATIVE_ZOOM, planPrefetch, prefetchTiles, tileCache } from "../lib/tileCache.js";

const MAX_TILES = 4000; // noin 90 Mt; yhden latauksen yläraja
const MIN_ZOOM_FLOOR = 10; // tätä karkeampia ruutuja ei kannata ladata

const mb = (bytes) => `${Math.max(1, Math.round(bytes / 1_000_000))} Mt`;

/** Ladattava alue: näkymä pienellä marginaalilla, zoomit nykyisestä tarkimpaan. */
export function planForView(map) {
  const b = map.getBounds().pad(0.1);
  const minZoom = Math.max(MIN_ZOOM_FLOOR, Math.min(MAX_NATIVE_ZOOM, Math.floor(map.getZoom()) - 1));
  return planPrefetch(
    { south: b.getSouth(), west: b.getWest(), north: b.getNorth(), east: b.getEast() },
    { minZoom, maxZoom: MAX_NATIVE_ZOOM, maxTiles: MAX_TILES },
  );
}

export function OfflineMapPanel({ layer, mapRef }) {
  const [stored, setStored] = useState(null); // ladattujen ruutujen määrä
  const [progress, setProgress] = useState(null); // { done, total } latauksen aikana
  const [result, setResult] = useState(null);
  const [plan, setPlan] = useState(() => (mapRef.current ? planForView(mapRef.current) : null));
  const controller = useRef(null);

  const refreshCount = () => tileCache.count().then(setStored);

  useEffect(() => {
    refreshCount();
    const m = mapRef.current;
    if (!m) return undefined;
    // Arvio päivittyy, kun karttaa liikutetaan paneelin ollessa auki.
    const update = () => setPlan(planForView(m));
    m.on("moveend", update);
    return () => {
      m.off("moveend", update);
      controller.current?.abort(); // paneelin sulkeminen ei jätä latausta pyörimään
    };
  }, []);

  const running = progress !== null;

  async function start() {
    const m = mapRef.current;
    if (!m || running) return;
    const chosen = planForView(m);
    if (chosen.tooLarge) return;
    controller.current = new AbortController();
    setResult(null);
    setProgress({ done: 0, total: chosen.count });
    logEvent("Offline-kartan lataus alkoi", `${layer}, ${chosen.count} ruutua, zoom ${chosen.zooms[0]}–${chosen.zooms.at(-1)}`);
    const outcome = await prefetchTiles({
      tiles: iterateTiles(chosen),
      total: chosen.count,
      layer,
      cache: tileCache,
      fetchTile: (z, x, y, signal) => fetchTileBlob(layer, z, x, y, signal),
      signal: controller.current.signal,
      onProgress: ({ done }) => setProgress({ done, total: chosen.count }),
    });
    setProgress(null);
    setResult(outcome);
    logEvent("Offline-kartan lataus päättyi", `${outcome.fetched} ladattu, ${outcome.skipped} oli jo, ${outcome.failed} epäonnistui`, outcome.failed ? "warn" : "info");
    refreshCount();
  }

  async function clear() {
    await tileCache.clear();
    setResult(null);
    refreshCount();
    logEvent("Offline-kartta tyhjennetty");
  }

  if (!tileCache.available()) {
    return <p class="s panel-note">Tämä selain ei tallenna karttaruutuja puhelimeen.</p>;
  }

  return (
    <div class="offline-box">
      {plan && !running && (
        <p class="s">
          {plan.tooLarge
            ? "Alue on liian laaja. Lähennä karttaa ja yritä uudelleen."
            : `Näkyvä alue: ${plan.count} ruutua, noin ${mb(plan.bytes)} (zoom ${plan.zooms[0]}–${plan.zooms.at(-1)}).${plan.capped ? " Alue on suuri, joten tarkimmat tasot jätetään pois." : ""}`}
        </p>
      )}

      {running ? (
        <>
          <progress class="offline-progress" max={progress.total} value={progress.done} aria-label="Offline-kartan lataus" />
          <p class="s" role="status">{progress.done} / {progress.total} ruutua</p>
          <button class="btn btn-block" onClick={() => controller.current?.abort()}>Keskeytä</button>
        </>
      ) : (
        <button class="btn btn-block" disabled={!plan || plan.tooLarge} onClick={start}>
          Lataa tämä alue offline-käyttöön
        </button>
      )}

      {result && !running && (
        <p class="s" role="status">
          {result.aborted
            ? `Keskeytetty. ${result.fetched} ruutua ehti latautua.`
            : result.stoppedByNetwork
              ? `Lataus pysähtyi, koska palvelin ei vastaa tai MML:n avain puuttuu. ${result.fetched} ruutua ladattu.`
              : `Valmis: ${result.fetched} ladattu${result.skipped ? `, ${result.skipped} oli jo puhelimessa` : ""}${result.failed ? `, ${result.failed} epäonnistui` : ""}.`}
        </p>
      )}

      <p class="s">
        {stored === null ? "" : stored === 0 ? "Puhelimessa ei ole ladattuja karttaruutuja." : `Puhelimessa ${stored} karttaruutua (noin ${mb(stored * APPROX_TILE_BYTES)}).`}
      </p>
      {stored > 0 && !running && (
        <button class="btn btn-quiet" onClick={clear}>Poista ladatut karttaruudut</button>
      )}
    </div>
  );
}
